import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon } from 'antd';
import { ColumnProps, PaginationConfig, SortOrder } from 'antd/lib/table';
import VARIANCE_COLORS from 'src/enums/varianceColor';
import { Sorting } from 'src/types/Sorting';
import FormattedNumber from 'src/components/common/FormattedNumber';
import { CURRENCY } from 'src/enums';
import { AlertResult, WML } from '../../types/AlertResults';
import TableColumnsFilter from 'src/components/common/TableColumnsFilter';
import styles from './AlertResultsTable.module.less';

const Colors = {
  similar: VARIANCE_COLORS.similar,
  positive: VARIANCE_COLORS.positive,
  negative: VARIANCE_COLORS.negative,
} as const;

enum PriceType {
  source_regular_price = 'Regular Price',
  source_sale_price = 'Sale Price',
  source_cart_price = 'Cart Price',
  source_coupon_price = 'Coupon Price',
  competitor_regular_price = 'Regular Price',
  competitor_sale_price = 'Sale Price',
  competitor_cart_price = 'Cart Price',
  competitor_coupon_price = 'Coupon Price',
}

export const defaultPagination: PaginationConfig = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  locale: { items_per_page: '' },
};

export type ColumnId = keyof AlertResult;

export type ColumnSorting = Sorting<AlertResult> & {
  field: ColumnId;
};

type StandardColumn = ColumnProps<AlertResult>;

type FilterableColumn = StandardColumn & { dataIndex: ColumnId };

export type AlertResultsColumn = FilterableColumn | StandardColumn;

export type ColumnsFilterOption = {
  id: ColumnId;
  name: string;
  disabled?: boolean;
};

export const defaultColumnIds: ColumnId[] = [
  'sourceProductName',
  'uniqueIdentifier',
  'sourceLowestPrice',
  'sourceLowestPriceType',
  'matchType',
  'sourceCompetitorLowestPriceVariance',
  'competitorOwnerName',
  'competitorProductName',
  'competitorLowestPrice',
  'competitorLowestPriceType',
];

export const columnsFilterOptions: ColumnsFilterOption[] = [
  {
    id: 'uniqueIdentifier',
    name: 'UID',
  },
  {
    id: 'categoryName',
    name: 'Category',
  },
  {
    id: 'sourceProductName',
    name: 'Product Name',
  },
  {
    id: 'sourceRegionName',
    name: 'Region',
  },
  {
    id: 'sourceRegularPrice',
    name: 'Regular Price',
  },
  {
    id: 'sourceLowestPrice',
    name: 'Lowest Price',
  },
  {
    id: 'sourceCompetitorPriceVariance',
    name: 'Regular Price Variance',
  },
  {
    id: 'sourceCompetitorLowestPriceVariance',
    name: 'Lowest Price Variance',
  },
  {
    id: 'matchType',
    name: 'Match Type',
  },
  {
    id: 'competitorOwnerName',
    name: 'Competitor Site',
  },
  {
    id: 'competitorSellerName',
    name: 'Competitor Seller',
  },
  {
    id: 'competitorFulfillerName',
    name: 'Competitor Fulfiller',
  },
  {
    id: 'competitorProductName',
    name: 'Competitor Product',
  },
  {
    id: 'competitorRegionName',
    name: 'Competitor Region',
  },
  {
    id: 'competitorRegularPrice',
    name: 'Competitor Regular Price',
  },
  {
    id: 'competitorLowestPrice',
    name: 'Competitor Lowest Price',
  },
];

const renderActionsTitle = (onColumnsFilterChange: (value: string) => void) => {
  return (
    <TableColumnsFilter
      onChange={onColumnsFilterChange}
      defaultColumns={defaultColumnIds}
      columnOptions={columnsFilterOptions}
    />
  );
};

const renderProductName = (record: AlertResult) => {
  return (
    <Link to={`/optiprice/product-details/${record.sourceProductId}`} className={styles.productName} target="_blank">
      {record.sourceProductName}
    </Link>
  );
};

const renderPrice = (price: number, currency: keyof typeof CURRENCY, type?: string, url?: string | null) => {
  const isRegularPrice = type === 'source_regular_price' || type === 'competitor_regular_price';

  return (
    <>
      <div className={styles.priceLabel}>
        <FormattedNumber type="currency" currency={currency} value={price} />
        {url && (
          <a href={url} className={styles.linkGlobe} target="_blank">
            <FontAwesomeIcon icon={['fal', 'globe']} />
          </a>
        )}
      </div>
      <div className={clsx(styles.priceType, isRegularPrice && styles.regular)}>
        {type && (
          <>
            {!isRegularPrice && <Icon type="tag" theme="filled" />}
            {PriceType[type as keyof typeof PriceType]}
          </>
        )}
      </div>
    </>
  );
};
const renderVariance = (variance: number) => {
  return (
    <>
      <div className={styles.priceLabel}>{variance}%</div>
      {variance > 0 ? (
        <span style={{ color: Colors.positive }}>{WML['l']}</span>
      ) : variance === 0 ? (
        <span style={{ color: Colors.similar }}>{WML['m']}</span>
      ) : (
        <span style={{ color: Colors.negative }}>{WML['w']}</span>
      )}
    </>
  );
};

const columns: FilterableColumn[] = [
  {
    title: 'UID',
    dataIndex: 'uniqueIdentifier',
    sorter: true,
  },
  {
    title: 'Category',
    dataIndex: 'categoryName',
    sorter: true,
  },
  {
    title: 'Product Name',
    dataIndex: 'sourceProductName',
    sorter: true,
    render: (_, record: AlertResult) => renderProductName(record),
  },
  {
    title: 'Region',
    dataIndex: 'sourceRegionName',
    sorter: true,
  },
  {
    title: 'Regular Price',
    dataIndex: 'sourceRegularPrice',
    sorter: true,
    render: (_, record: AlertResult) => renderPrice(record.sourceRegularPrice, record.sourceCurrency),
  },
  {
    title: 'Lowest Price',
    dataIndex: 'sourceLowestPrice',
    sorter: true,
    render: (_, record: AlertResult) =>
      renderPrice(
        record.sourceLowestPrice,
        record.sourceCurrency,
        record.sourceLowestPriceType,
        record.sourceProductUrl
      ),
  },
  {
    title: 'Regular Price Variance',
    dataIndex: 'sourceCompetitorPriceVariance',
    sorter: true,
    render: (_, record: AlertResult) => renderVariance(record.sourceCompetitorPriceVariance),
  },
  {
    title: 'Lowest Price Variance',
    dataIndex: 'sourceCompetitorLowestPriceVariance',
    sorter: true,
    render: (_, record: AlertResult) => renderVariance(record.sourceCompetitorLowestPriceVariance),
  },
  {
    title: 'Match Type',
    dataIndex: 'matchType',
    sorter: true,
    render: (_, record: AlertResult) => (record.matchType === 'LIKE' ? 'SMART' : record.matchType),
  },
  {
    title: 'Competitor Site',
    dataIndex: 'competitorOwnerName',
    sorter: true,
  },
  {
    title: 'Competitor Seller',
    dataIndex: 'competitorSellerName',
    sorter: true,
  },
  {
    title: 'Competitor Fulfiller',
    dataIndex: 'competitorFulfillerName',
    sorter: true,
  },
  {
    title: 'Competitor Product',
    dataIndex: 'competitorProductName',
    sorter: true,
    render: (_, record: AlertResult) => <div className={styles.productName}>{record.competitorProductName}</div>,
  },
  {
    title: 'Competitor Region',
    dataIndex: 'competitorRegionName',
    sorter: true,
  },
  {
    title: 'Competitor Regular Price',
    dataIndex: 'competitorRegularPrice',
    sorter: true,
    render: (_, record: AlertResult) => renderPrice(record.competitorRegularPrice, record.competitorCurrency),
  },
  {
    title: 'Competitor Lowest Price',
    dataIndex: 'competitorLowestPrice',
    sorter: true,
    render: (_, record: AlertResult) =>
      renderPrice(
        record.competitorLowestPrice,
        record.competitorCurrency,
        record.competitorLowestPriceType,
        record.competitorProductUrl
      ),
  },
];

export const getColumns = (
  sorting: ColumnSorting,
  selectedColumnIds: ColumnId[],
  onColumnsFilterChange: (value: string) => void
): AlertResultsColumn[] => {
  const sortOrder = (key: ColumnId): SortOrder | false => {
    return sorting.field === key ? sorting.order : false;
  };

  const filteredColumns = columns
    .filter((column) => selectedColumnIds.includes(column.dataIndex))
    .map((column) => ({
      ...column,
      ...(column.sorter && { sortOrder: sortOrder(column.dataIndex) }),
    }));

  return [
    ...filteredColumns,
    {
      title: renderActionsTitle(onColumnsFilterChange),
      key: 'actions',
      fixed: 'right',
    },
  ];
};
